'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { openCheckoutForDescriptor } from '@/shared/lib/paddle';
import type { CheckoutKind } from '@/shared/lib/paddle';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import type { CheckoutAlternative } from '@/types/checkout';

/**
 * Payment-rail chooser shown when the API shipped a `checkout_alternative`
 * next to the primary (Paddle) descriptor — RU-locale buyers holding an
 * RF-issued card pay in rubles via T-Bank while everyone else stays on Paddle.
 *
 * Appears AFTER the buyer taps the price CTA (instead of a permanent link
 * under every card): the tap opens this modal and the buyer picks the rail.
 * The T-Bank `label` (with the exact RUB amount) and `hint` are
 * server-localized copy rendered verbatim (API-driven messages rule).
 *
 * T-Bank is a full-page redirect: no overlay listeners — the return page and
 * the notification webhook own the rest, so the launching state only guards
 * against double taps until navigation happens.
 *
 * MODAL-LOCK INVARIANT (see memory project_web_paddle_drawer_modal_lock): this
 * dialog is rendered by a CTA that lives INSIDE a vaul Drawer paywall, so it
 * stacks a second modal over the sheet. It MUST stay `modal={false}`: a modal
 * Radix Dialog sets `body{pointer-events:none}` on open and RESTORES the value
 * captured at open time on close — and that captured value is `none` (the
 * Drawer was already modal). That stale restore races the Drawer's own
 * `modal={!checkoutActive}` relax (a one-shot rAF that sets body pe:auto) and,
 * when it lands last, re-locks the body so Paddle's body-level checkout iframe
 * becomes untappable and the buyer silently cannot pay. Non-modal means Radix
 * never touches body pointer-events, so there is nothing to clobber the relax.
 * The explicit `pointer-events-auto` on the overlay + content keeps THIS
 * dialog's own controls clickable over the Drawer's locked body.
 */
export function CheckoutRailChooserDialog({
  open,
  onOpenChange,
  alternative,
  kind,
  projectId,
  primaryPriceLabel,
  onPickPrimary,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alternative: CheckoutAlternative;
  kind: CheckoutKind;
  /** Building id — required only for the building-bound pack session. */
  projectId?: string;
  /** Formatted primary (Paddle) price appended to the international-card
   *  option so both rails show their exact amount side by side. */
  primaryPriceLabel?: string;
  /** Buyer picked the international (Paddle) rail — the host closes this
   *  dialog, relaxes its modal lock if any, and launches its own checkout. */
  onPickPrimary: () => void;
}) {
  const t = useTranslations('railChooser');
  const [isLaunching, setIsLaunching] = useState(false);

  const pickAlternative = () => {
    setIsLaunching(true);
    void openCheckoutForDescriptor(alternative, { kind, projectId })
      // Redirect leaves the page on success; reaching catch means the session
      // call failed — surface the server-provided message (API-driven) and
      // re-enable so the buyer can retry.
      .catch((error) => {
        if (error instanceof ApiClientError) {
          toast.error(error.message);
        }
        setIsLaunching(false);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !isLaunching && onOpenChange(next)}
      // Non-modal on purpose — see the MODAL-LOCK INVARIANT above. Radix must
      // not manage body pointer-events here, or its close-time restore re-locks
      // the page over the Paddle checkout iframe.
      modal={false}
    >
      <DialogContent
        showCloseButton
        // The host Drawer sets body{pointer-events:none} while modal; a
        // non-modal Radix layer does NOT re-enable it, so pin it explicitly on
        // this dialog's own surface and backdrop so its controls stay tappable.
        className="pointer-events-auto max-w-sm"
        overlayClassName="pointer-events-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-center">{t('title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-1">
          <button
            type="button"
            disabled={isLaunching}
            onClick={onPickPrimary}
            className="inline-flex min-h-11 w-full items-center justify-center whitespace-normal rounded-full bg-primary px-6 py-2 text-center text-list-item-value font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {primaryPriceLabel
              ? `${t('internationalCard')} · ${primaryPriceLabel}`
              : t('internationalCard')}
          </button>
          <p className="text-center text-list-item-subtitle text-muted-foreground">
            {t('internationalCardHint')}
          </p>
          <button
            type="button"
            disabled={isLaunching}
            onClick={pickAlternative}
            className="inline-flex min-h-11 w-full items-center justify-center whitespace-normal rounded-full border border-primary px-6 py-2 text-center text-list-item-value font-semibold text-primary transition-colors hover:bg-primary/5 disabled:opacity-60"
          >
            {alternative.label}
          </button>
          <p className="text-center text-list-item-subtitle text-muted-foreground">
            {alternative.hint}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
